import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SignupFlow } from "../../components/signup/SignupFlow";
import { useGetCheckoutSession } from "../../services/signup";
import { SignupState, STEPS } from "../../components/signup/types";
import useMetaTracking, {
  META_PIXEL_ID,
} from "../../components/hooks/useMetaTracking";
import useGoogleTracking, {
  GOOGLE_CONVERSION_LABEL_PAYMENT,
  GOOGLE_TAG_ID,
  googleEventNamePayment,
  googleEventNamePageLoad,
} from "../../components/hooks/useGoogleTracking";
// import useTikTokTracking, {
//   TIKTOK_PIXEL_ID,
//   TikTokEvents,
// } from "../../components/hooks/useTikTokTracking";
import { UPGRADE_FLOW } from "../../components/signup/flow";
import { buildStateFromProto } from "../../components/signup/utils";
import Loading from "../../components/common/Loading";

export default ({
  includeMembershipStep = false,
}: {
  includeMembershipStep?: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isDebug = !!queryParams.get("debug");

  const [initialState, setInitialState] = useState<
    [STEPS | undefined, SignupState] | null
  >(null);
  const { request, loading } = useGetCheckoutSession();
  const { track: trackMeta } = useMetaTracking(META_PIXEL_ID);
  const { fireConversionEvent, firePageLoadEvent } =
    useGoogleTracking(GOOGLE_TAG_ID);
  // const { track: trackTikTok } = useTikTokTracking(TIKTOK_PIXEL_ID);
  const membership = location.state?.membership;
  let currentFlow = [...UPGRADE_FLOW];
  // If membership is already selected (passed via params or existing state), skip membership step
  let initialStep = STEPS.MEMBERSHIP;
  if (!includeMembershipStep) {
    initialStep = STEPS.UPGRADE_PAYMENT;
    currentFlow = currentFlow.filter((s) => s.name !== STEPS.MEMBERSHIP);
    // Update PAYMENT step to have no back step so it triggers onExit
    const paymentStepIndex = currentFlow.findIndex(
      (s) => s.name === STEPS.UPGRADE_PAYMENT,
    );
    if (paymentStepIndex !== -1) {
      currentFlow[paymentStepIndex] = {
        ...currentFlow[paymentStepIndex],
        defaultBackStep: null,
        backDisabled: false,
      };
    }
  }
  const validSteps = new Set(currentFlow.map((s) => s.name));

  const updateStep = useCallback((_step: string) => {
    // We don't really need to update the URL for checkout flow as it's a single page app feel,
    // but if we wanted to support deep linking we could.
    // For now, let's just keep it simple.
  }, []);

  const onSignupComplete = useCallback(() => {
    fireConversionEvent(
      GOOGLE_CONVERSION_LABEL_PAYMENT,
      googleEventNamePayment(),
    );
    trackMeta("Purchase");
    // trackTikTok(TikTokEvents.Purchase);
  }, [fireConversionEvent, trackMeta]);

  useEffect(() => {
    firePageLoadEvent(googleEventNamePageLoad("checkout"));
  }, []);

  useEffect(() => {
    const fetch = async () => {
      // GetCheckoutSession just fetches current state, doesn't need params
      const resp = await request(undefined);

      if (!resp) return;
      const signupState: SignupState = resp.state
        ? buildStateFromProto(resp.state)
        : {};

      if (membership) {
        signupState.membership = membership;
      }

      setInitialState([
        resp.state?.stepName && validSteps.has(resp.state?.stepName as STEPS)
          ? (resp.state?.stepName as STEPS)
          : initialStep,
        signupState,
      ]);
    };

    if (isDebug) {
      setInitialState([STEPS.MEMBERSHIP, {}]);
    } else {
      fetch();
    }
  }, []);

  if (!initialState || loading) {
    return <Loading />;
  }
  const [step, formData] = initialState;

  return (
    <SignupFlow
      signupFlow={currentFlow}
      initialStep={step}
      initialFormData={formData}
      setStepName={updateStep}
      isDebug={isDebug}
      onSignupComplete={onSignupComplete}
      onExit={() => navigate(-1)}
      useAuthenticatedUpdate={true}
    />
  );
};
